import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Mic, MicOff, Video, VideoOff } from "lucide-react";

export type LobbyDevices = { microphone: boolean; camera: boolean };

type MeetingLobbyProps = {
    name: string;
    onNameChange: (name: string) => void;
    devices: LobbyDevices;
    onDevicesChange: (devices: LobbyDevices) => void;
    joining: boolean;
    error: string | null;
    onJoin: () => void;
};

/**
 * What the join link opens, before anyone can see or hear you.
 *
 * The lobby used to be a name and a button, and pressing the button connected
 * with the camera and the microphone already live. For a general product that
 * is a convenience worth arguing about; for someone arriving at a therapy
 * session it is the difference between composing yourself and being on camera
 * before you meant to be. Meet, Zoom, Whereby and Doxy.me all put a device
 * preview in this exact spot.
 *
 * The preview is local. Nothing is published, nothing is sent, and there is no
 * room yet — `getUserMedia` here is the same permission prompt the call would
 * raise a second later, moved to where the patient can answer it calmly.
 */
export default function MeetingLobby({
    name,
    onNameChange,
    devices,
    onDevicesChange,
    joining,
    error,
    onJoin,
}: MeetingLobbyProps) {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    /**
     * Whether the browser gave us a camera at all. A patient who refused the
     * permission, or has no camera, should still be able to join — so this only
     * decides what the preview shows, never whether the button works.
     */
    const [cameraDenied, setCameraDenied] = useState(false);

    useEffect(() => {
        // Nothing to preview with the camera off, and holding a live camera the
        // patient just switched off would be exactly the thing this screen
        // exists to prevent.
        if (!devices.camera) {
            streamRef.current?.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
            return;
        }

        let cancelled = false;
        navigator.mediaDevices
            ?.getUserMedia({ video: true })
            .then((stream) => {
                if (cancelled) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }
                streamRef.current = stream;
                setCameraDenied(false);
                if (videoRef.current) videoRef.current.srcObject = stream;
            })
            .catch(() => {
                // Refused, missing, or already held by another tab. The lobby
                // says so rather than showing a black rectangle.
                if (!cancelled) setCameraDenied(true);
            });

        return () => {
            cancelled = true;
            streamRef.current?.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        };
    }, [devices.camera]);

    // The room asks for its own tracks the moment it connects, and two holders
    // of the same camera is how a preview ends up frozen inside the call.
    const join = () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        onJoin();
    };

    const toggle = (key: keyof LobbyDevices) =>
        onDevicesChange({ ...devices, [key]: !devices[key] });

    const showPreview = devices.camera && !cameraDenied;

    return (
        /* Scrolls, and centres only when there is room to.
           The room itself does not scroll — a call that can be dragged off
           the screen mid-session is worse than one that cannot — so the one
           screen in it that can outgrow a phone has to say so itself: the
           preview, the name, two device toggles and the join button do not
           fit down 844px with the keyboard up. `m-auto` is what keeps it
           centred on a laptop without pinning the top of it out of reach on
           a handset. */
        <div className="flex flex-1 justify-center overflow-y-auto p-4 sm:p-6">
            <div className="m-auto w-full max-w-2xl">
                {/* White for the same reason the ended screen is: the room is
                    dark whatever the visitor's theme says. */}
                <img
                    src="/images/logo/logo.png"
                    alt="Alchely"
                    width={130}
                    height={32}
                    className="mx-auto mb-6 brightness-0 invert sm:mb-8"
                />

                <div className="grid gap-6 sm:grid-cols-[1.15fr_1fr] sm:items-center">
                    <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-room-line bg-room-raised">
                        {showPreview ? (
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                /* Mirrored, because a preview that is not is a
                                   preview nobody recognises as themselves. */
                                className="h-full w-full scale-x-[-1] object-cover"
                                aria-label={t("guestMeeting.preview")}
                            />
                        ) : (
                            <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
                                <VideoOff
                                    size={22}
                                    className="text-room-ink-muted"
                                    aria-hidden="true"
                                />
                                <p className="text-xs text-room-ink-muted">
                                    {cameraDenied
                                        ? t("guestMeeting.cameraUnavailable")
                                        : t("guestMeeting.cameraOff")}
                                </p>
                            </div>
                        )}
                    </div>

                    <div>
                        <h1 className="mb-1 text-lg font-semibold tracking-tight text-room-ink">
                            {t("guestMeeting.title")}
                        </h1>
                        <p className="mb-5 text-sm leading-relaxed text-room-ink-muted">
                            {t("guestMeeting.subtitle")}
                        </p>

                        <label
                            htmlFor="guest-name"
                            className="mb-1.5 block text-xs font-semibold text-room-ink-muted"
                        >
                            {t("guestMeeting.yourName")}
                        </label>
                        <input
                            id="guest-name"
                            type="text"
                            value={name}
                            onChange={(event) => onNameChange(event.target.value)}
                            onKeyDown={(event) => event.key === "Enter" && join()}
                            maxLength={80}
                            placeholder={t("guestMeeting.namePlaceholder")}
                            className="room-field mb-3"
                        />

                        {/* State is in the icon and the label as well as the
                            colour: whether your microphone is on is not
                            something to convey with a hue alone (WCAG 1.4.1). */}
                        <div className="mb-4 flex gap-2">
                            <button
                                type="button"
                                onClick={() => toggle("microphone")}
                                aria-pressed={devices.microphone}
                                className={`room-control flex-1 ${
                                    devices.microphone ? "" : "room-control-off"
                                }`}
                            >
                                {devices.microphone ? (
                                    <Mic size={16} aria-hidden="true" />
                                ) : (
                                    <MicOff size={16} aria-hidden="true" />
                                )}
                                <span>
                                    {t(
                                        devices.microphone
                                            ? "meetingControls.microphone"
                                            : "meetingControls.microphoneMuted"
                                    )}
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => toggle("camera")}
                                aria-pressed={devices.camera}
                                className={`room-control flex-1 ${
                                    devices.camera ? "" : "room-control-off"
                                }`}
                            >
                                {devices.camera ? (
                                    <Video size={16} aria-hidden="true" />
                                ) : (
                                    <VideoOff size={16} aria-hidden="true" />
                                )}
                                <span>
                                    {t(
                                        devices.camera
                                            ? "meetingControls.camera"
                                            : "meetingControls.cameraOff"
                                    )}
                                </span>
                            </button>
                        </div>

                        {error && (
                            <p
                                role="alert"
                                className="mb-3 text-sm leading-relaxed text-room-live"
                            >
                                {error}
                            </p>
                        )}

                        <button
                            type="button"
                            onClick={join}
                            disabled={joining}
                            className="room-control room-control-primary w-full disabled:opacity-50"
                        >
                            {joining && (
                                <Loader2
                                    className="h-4 w-4 animate-spin"
                                    aria-hidden="true"
                                />
                            )}
                            <span>{t("guestMeeting.join")}</span>
                        </button>

                        <p className="mt-2.5 text-xs leading-relaxed text-room-ink-muted">
                            {t("guestMeeting.checkDevices")}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
